"use client";

import React, { useState, useEffect, useRef } from "react";
import Image from "next/image";
import Link from "next/link";
import { motion } from "framer-motion";
import {
  useHydratedScroll,
  useHydratedParallax,
} from "@/hooks/useHydratedScroll";
import { getImageUrl } from "@/utils/utils";
import CountUp from "react-countup";

interface HomePageAboutClientProps {
  lang: string;
  homepageContent: any;
}

export default function HomePageAboutClient({
  lang,
  homepageContent,
}: HomePageAboutClientProps) {
  const [statsVisible, setStatsVisible] = useState(true); // Temporarily set to true for debugging
  const [statsKey, setStatsKey] = useState(0);
  const [isMobile, setIsMobile] = useState(false);
  const statsRef = useRef<HTMLDivElement>(null);

  // State to track if component is mounted (prevents hydration errors)
  const [isMounted, setIsMounted] = useState(false);

  useEffect(() => {
    setIsMounted(true);
  }, []);

  // Hydration-safe scroll hooks
  const { scrollYProgress: aboutScrollProgress, isMounted: aboutMounted } =
    useHydratedScroll({
      target: statsRef,
      offset: ["-200px end", "end -200px"],
    });

  const aboutBackgroundY = useHydratedParallax(
    aboutScrollProgress,
    ["-30%", "30%"],
    aboutMounted
  );

  // Animation variants for bottom-to-top reveal
  const sectionVariants = {
    hidden: {
      opacity: 0,
      y: isMobile ? 30 : 60, // Reduced movement on mobile
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: isMobile ? 0.6 : 0.8, // Faster animation on mobile
        ease: "easeOut" as const,
      },
    },
  };

  const staggerContainer = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
      },
    },
  };

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        console.log(
          "Stats intersection observer triggered:",
          entry.isIntersecting
        );
        // When stats section visibility changes
        if (entry.isIntersecting) {
          console.log("Stats section is visible, setting statsVisible to true");
          // Set visible and increment key to restart animation
          setStatsVisible(true);
          setStatsKey((prevKey) => prevKey + 1);
        }
        // Note: Removed the else clause to keep stats visible once they've been shown
      },
      {
        root: null,
        rootMargin: "100px", // Trigger earlier when element is 100px away from viewport
        threshold: 0.05, // Trigger when only 5% of the element is visible
      }
    );

    if (statsRef.current) {
      console.log("Observing stats ref:", statsRef.current);
      observer.observe(statsRef.current);
    } else {
      console.log("statsRef.current is null");
    }

    // Fallback: Set stats visible after a delay as backup
    const fallbackTimer = setTimeout(() => {
      console.log("Fallback timer triggered, setting statsVisible to true");
      setStatsVisible(true);
      setStatsKey((prevKey) => prevKey + 1);
    }, 3000);

    return () => {
      if (statsRef.current) {
        observer.unobserve(statsRef.current);
      }
      clearTimeout(fallbackTimer);
    };
  }, []);

  // Mobile detection for optimized parallax
  useEffect(() => {
    const checkDevice = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    checkDevice();
    window.addEventListener("resize", checkDevice);

    return () => window.removeEventListener("resize", checkDevice);
  }, []);

  return (
    <motion.section
      ref={statsRef}
      className="w-full relative"
      style={{
        backgroundColor: "#F4F5F6",
        paddingTop: "60px",
        paddingBottom: "60px",
      }}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, amount: 0.1 }} // Reduced from 0.3 to 0.1 for earlier trigger
      variants={sectionVariants}
    >
      {/* Parallax Background Image with Overlay */}
      <div className="absolute inset-0 w-full h-full sm:h-[calc(100%-200px)] lg:h-[1000px] overflow-hidden">
        <motion.div
          className="absolute w-full h-[120%] -top-[10%] sm:h-[180%] sm:-top-[40%] lg:h-[130%] lg:-top-[15%]" // Better mobile scaling
          style={{
            y: aboutMounted ? aboutBackgroundY : 0,
          }}
        >
          {homepageContent?.about?.backgroundImage && (
            <Image
              src={getImageUrl(
                homepageContent?.about?.backgroundImage?.url || ""
              )}
              alt="Background"
              fill
              className="object-cover object-center"
              priority
            />
          )}
        </motion.div>
        {/* Header-aware top overlay */}
        <div className="parallax-header-overlay"></div>
        {/* Main overlay */}
        <div className="absolute inset-0 bg-black opacity-70 z-10"></div>
      </div>

      <div className="relative z-10 max-w-[1316px] mx-auto px-4 sm:px-6 lg:px-6">
        <div className="flex flex-col items-center gap-12 sm:gap-16 lg:gap-[100px] py-8 sm:py-12 lg:py-16">
          {/* Company Info Section */}
          <div className="flex flex-col items-center gap-6 sm:gap-8 max-w-[1019px] w-full">
            <div className="text-white text-center text-[16px] sm:text-[18px] lg:text-[20px] font-medium leading-[24px] sm:leading-[27px] lg:leading-[30px] opacity-80 px-4">
              {homepageContent?.about?.description || ""}
            </div>

            {/* About Us Button */}
            <Link
              href={`/${lang}/about`}
              className="group inline-flex items-center gap-2 sm:gap-3 px-4 sm:px-6 py-3 sm:py-4 rounded-[30px] bg-primary-800 text-white hover:bg-white hover:text-primary-800 transition-all duration-300 shadow-lg cursor-pointer"
            >
              <svg
                width="8"
                height="14"
                viewBox="0 0 8 14"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                className="transition-colors duration-300 w-2 h-3.5"
              >
                <path
                  d="M7.41793 7.37499L2.5406 12.2107C2.3326 12.4188 1.99927 12.4188 1.8326 12.2107L1.5406 11.9186C1.49171 11.8736 1.45267 11.819 1.42598 11.7581C1.39928 11.6973 1.38549 11.6315 1.38549 11.5651C1.38549 11.4986 1.39928 11.4329 1.42598 11.372C1.45267 11.3112 1.49171 11.2566 1.5406 11.2116L5.79393 7.00013L1.5406 2.83138C1.49149 2.78639 1.45228 2.73167 1.42545 2.67069C1.39862 2.60972 1.38477 2.54383 1.38477 2.47721C1.38477 2.41058 1.39862 2.34469 1.42545 2.28372C1.45228 2.22274 1.49149 2.16802 1.5406 2.12303L1.83393 1.83088C2.0006 1.62278 2.33393 1.62278 2.54193 1.83088L7.4606 6.66663C7.54472 6.76805 7.58713 6.89769 7.5792 7.02925C7.57128 7.1608 7.51362 7.28441 7.41793 7.37499Z"
                  fill="currentColor"
                />
              </svg>
              <span className="font-inter text-[14px] sm:text-[16px] font-medium leading-[16px] capitalize">
                {homepageContent?.about?.ctaText || "About Us"}
              </span>
            </Link>
          </div>

          {/* Stats Container */}
          <motion.div
            ref={statsRef}
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 lg:gap-8 w-full max-w-5xl"
            variants={staggerContainer}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.3 }}
          >
            {/* Overall Projects */}
            <motion.div
              className="flex flex-col items-center justify-center h-[280px] lg:h-[300px] p-6 lg:p-7 rounded-[20px] border border-white"
              variants={sectionVariants}
            >
              <div className="flex flex-col items-center gap-8 lg:gap-10">
                {homepageContent?.about?.cards?.card1.image && (
                  <Image
                    src={getImageUrl(
                      homepageContent?.about?.cards?.card1.image?.url || ""
                    )}
                    alt="Overall Projects"
                    width={60}
                    height={60}
                  />
                )}
                <div className="flex flex-col items-center gap-2">
                  <div className="flex items-center justify-center">
                    {(() => {
                      return statsVisible;
                    })() ? (
                      <>
                        <span className="text-white text-[40px] lg:text-[50px] font-medium leading-[48px] lg:leading-[58px] tracking-[-1.44px]">
                          <CountUp
                            key={statsKey}
                            start={0}
                            end={
                              homepageContent?.about?.cards?.card1?.value || 0
                            }
                            duration={2.5}
                          />
                        </span>
                        <span className="text-white text-[40px] lg:text-[50px] font-medium leading-[48px] lg:leading-[58px] tracking-[-1.44px]">
                          +
                        </span>
                      </>
                    ) : (
                      <>
                        <span className="text-white text-[40px] lg:text-[50px] font-medium leading-[48px] lg:leading-[58px] tracking-[-1.44px]">
                          0
                        </span>
                        <span className="text-white text-[40px] lg:text-[50px] font-medium leading-[48px] lg:leading-[58px] tracking-[-1.44px]">
                          +
                        </span>
                      </>
                    )}
                  </div>
                  <div className="text-white text-center text-[16px] font-normal leading-[17.6px] capitalize">
                    {homepageContent?.about?.cards?.card1?.label ||
                      "Overall Projects"}
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Employees */}
            <motion.div
              className="flex flex-col items-center justify-center h-[280px] lg:h-[300px] p-6 lg:p-7 rounded-[20px] border border-white"
              variants={sectionVariants}
            >
              <div className="flex flex-col items-center gap-8 lg:gap-10">
                {homepageContent?.about?.cards?.card2.image && (
                  <Image
                    src={getImageUrl(
                      homepageContent?.about?.cards?.card2.image?.url || ""
                    )}
                    alt="Employees"
                    width={60}
                    height={60}
                  />
                )}
                <div className="flex flex-col items-center gap-2">
                  <div className="flex items-center justify-center">
                    {statsVisible ? (
                      <>
                        <span className="text-white text-[40px] lg:text-[50px] font-medium leading-[48px] lg:leading-[58px] tracking-[-1.44px]">
                          <CountUp
                            key={statsKey}
                            start={0}
                            end={
                              homepageContent?.about?.cards?.card2?.value || 0
                            }
                            duration={2.5}
                          />
                        </span>
                        <span className="text-white text-[40px] lg:text-[50px] font-medium leading-[48px] lg:leading-[58px] tracking-[-1.44px]">
                          +
                        </span>
                      </>
                    ) : (
                      <>
                        <span className="text-white text-[40px] lg:text-[50px] font-medium leading-[48px] lg:leading-[58px] tracking-[-1.44px]">
                          0
                        </span>
                        <span className="text-white text-[40px] lg:text-[50px] font-medium leading-[48px] lg:leading-[58px] tracking-[-1.44px]">
                          +
                        </span>
                      </>
                    )}
                  </div>
                  <div className="text-white text-center text-[16px] font-normal leading-[17.6px] capitalize">
                    {homepageContent?.about?.cards?.card2?.label ||
                      "Employees"}
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Award */}
            <motion.div
              className="flex flex-col items-center justify-center h-[280px] lg:h-[300px] p-6 lg:p-7 rounded-[20px] border border-white"
              variants={sectionVariants}
            >
              <div className="flex flex-col items-center gap-8 lg:gap-10">
                {homepageContent?.about?.cards?.card3?.image && (
                  <Image
                    src={getImageUrl(
                      homepageContent?.about?.cards?.card3?.image?.url || ""
                    )}
                    alt="Award"
                    width={60}
                    height={60}
                  />
                )}
                <div className="flex flex-col items-center gap-2">
                  <div className="flex items-center justify-center">
                    {statsVisible ? (
                      <>
                        <span className="text-white text-[40px] lg:text-[50px] font-medium leading-[48px] lg:leading-[58px] tracking-[-1.44px]">
                          <CountUp
                            key={statsKey}
                            start={0}
                            end={
                              homepageContent?.about?.cards?.card3?.value || 0
                            }
                            duration={2.5}
                          />
                        </span>
                        <span className="text-white text-[40px] lg:text-[50px] font-medium leading-[48px] lg:leading-[58px] tracking-[-1.44px]">
                          +
                        </span>
                      </>
                    ) : (
                      <>
                        <span className="text-white text-[40px] lg:text-[50px] font-medium leading-[48px] lg:leading-[58px] tracking-[-1.44px]">
                          0
                        </span>
                        <span className="text-white text-[40px] lg:text-[50px] font-medium leading-[48px] lg:leading-[58px] tracking-[-1.44px]">
                          +
                        </span>
                      </>
                    )}
                  </div>
                  <div className="text-white text-center text-[16px] font-normal leading-[17.6px] capitalize">
                    {homepageContent?.about?.cards?.card3?.label || "Award"}
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Years in Business */}
            <motion.div
              className="flex flex-col items-center justify-center h-[280px] lg:h-[300px] p-6 lg:p-7 rounded-[20px] border border-white"
              variants={sectionVariants}
            >
              <div className="flex flex-col items-center gap-8 lg:gap-10">
                {homepageContent?.about?.cards?.card4?.image && (
                  <Image
                    src={getImageUrl(
                      homepageContent?.about?.cards?.card4.image?.url || ""
                    )}
                    alt="Years in Business"
                    width={60}
                    height={60}
                  />
                )}
                <div className="flex flex-col items-center gap-2">
                  <div className="flex items-center justify-center">
                    {statsVisible ? (
                      <>
                        <span className="text-white text-[40px] lg:text-[50px] font-medium leading-[48px] lg:leading-[58px] tracking-[-1.44px]">
                          <CountUp
                            key={statsKey}
                            start={0}
                            end={
                              homepageContent?.about?.cards?.card4?.value || 0
                            }
                            duration={2.5}
                          />
                        </span>
                        <span className="text-white text-[40px] lg:text-[50px] font-medium leading-[48px] lg:leading-[58px] tracking-[-1.44px]">
                          +
                        </span>
                      </>
                    ) : (
                      <>
                        <span className="text-white text-[40px] lg:text-[50px] font-medium leading-[48px] lg:leading-[58px] tracking-[-1.44px]">
                          0
                        </span>
                        <span className="text-white text-[40px] lg:text-[50px] font-medium leading-[48px] lg:leading-[58px] tracking-[-1.44px]">
                          +
                        </span>
                      </>
                    )}
                  </div>
                  <div className="text-white text-center text-[16px] font-normal leading-[17.6px] capitalize">
                    {homepageContent?.about?.cards?.card4?.label ||
                      "Years in Business"}
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>

          {/* Mission and Vision Section */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 sm:gap-8 lg:gap-16 w-full max-w-6xl">
            {/* Mission */}
            <div className="flex flex-col items-center gap-4 sm:gap-6 lg:gap-7">
              <a
                href={`/${lang}/about#mission-vision`}
                className="block w-full max-w-[658px] h-[240px] sm:h-[280px] lg:h-[370px] bg-white rounded-lg overflow-hidden relative group br-20"
              >
                {homepageContent?.about?.mission?.image && (
                  <Image
                    src={getImageUrl(
                      homepageContent?.about?.mission?.image?.url || ""
                    )}
                    alt="Mission"
                    fill
                    draggable={false}
                    className="object-cover transition-transform duration-300 group-hover:scale-105"
                  />
                )}
                <div
                  style={{
                    borderRadius: "20px",
                    opacity: 0.3,
                    background: "var(--Base-Black, #000)",
                  }}
                  className="absolute inset-0 pointer-events-none"
                ></div>
              </a>
              <h3 className="text-white lg:text-gray-800 text-center text-[20px] sm:text-[22px] lg:text-[24px] font-medium leading-[24px] sm:leading-[26px] lg:leading-[28.8px] tracking-[-0.6px] sm:tracking-[-0.66px] lg:tracking-[-0.72px]">
                {homepageContent?.about?.mission?.label || "Mission"}
              </h3>
            </div>

            {/* Vision */}
            <div className="flex flex-col items-center gap-4 sm:gap-6 lg:gap-7">
              <a
                href={`/${lang}/about#mission-vision`}
                className="block w-full max-w-[658px] h-[240px] sm:h-[280px] lg:h-[370px] bg-white rounded-lg overflow-hidden relative group br-20"
              >
                {homepageContent?.about?.vision?.image && (
                  <Image
                    src={getImageUrl(
                      homepageContent?.about?.vision?.image?.url || ""
                    )}
                    alt="Vision"
                    fill
                    draggable={false}
                    className="object-cover transition-transform duration-300 group-hover:scale-105"
                  />
                )}
                <div
                  style={{
                    borderRadius: "20px",
                    opacity: 0.3,
                    background: "var(--Base-Black, #000)",
                  }}
                  className="absolute inset-0 pointer-events-none"
                ></div>
              </a>
              <h3 className="text-white sm:text-gray-800 text-center text-[20px] sm:text-[22px] lg:text-[24px] font-medium leading-[24px] sm:leading-[26px] lg:leading-[28.8px] tracking-[-0.6px] sm:tracking-[-0.66px] lg:tracking-[-0.72px]">
                {homepageContent?.about?.vision?.label}
              </h3>
            </div>
          </div>
        </div>
      </div>
    </motion.section>
  );
}